import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomTooltip,
  DropdownElement,
  EIconName,
  NumberInput,
  SwitchElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './cost-session-item.scss';

// Models
import {
  ICostPerSessionCreate, ICostPerSessionRates,
  ICostPerSessionUpdate,
  IPerSessionRateItem
} from '../../../../interfaces/counsellors.interfaces';
import { TSessionType } from '../../../../types/settings.types';
import { ICurrency } from '../../../../interfaces/company.interfaces';

// Utils
import { numToFixed } from '../../../../utils/math';
import { replaceCurrencySymbol } from '../../../../utils/currency';

type TFormValue = {
  rate_type: TSessionType | '--',
  call_duration: number | string | '--',
  rate: number | string,
  profit: number | string,
  free_service_minutes: number,
  prepaid_enabled: boolean,
  free_minutes_enabled: boolean,
  full_free_service_enabled: boolean,
};

export type TCostSessionItemRef = {
  isHasChanges: () => boolean;
  sessionIsNullish: () => boolean;
  resetState: () => void;
};

type TCostSessionItemProps = {
  worker_id: number,
  percentFee: number,
  transactionFee: number,
  currency: ICurrency,
  durations: {
    audio_call: number[],
    video_call: number[],
    conversation: number[],
  };
  existDurations?: {
    audio_call: number[],
    video_call: number[],
    conversation: number[],
  };
  isNullishItem?: boolean,
  isFirstItem?: boolean,
  session?: IPerSessionRateItem;
  loading?: boolean,
  createSession?: (data: ICostPerSessionCreate) => Promise<{ payload: ICostPerSessionRates | null }>;
  updateSession?: (data: ICostPerSessionUpdate) => Promise<{ payload: ICostPerSessionRates | null }>;
  toggleSession?: (data: ICostPerSessionUpdate, checked: boolean, event: any) => void;
  deleteSession?: () => void;
};

export const CostSessionItem = forwardRef<TCostSessionItemRef, TCostSessionItemProps>((
    {
      worker_id,
      durations,
      currency,
      percentFee,
      transactionFee,
      session,
      existDurations = {
        audio_call: [],
        video_call: [],
        conversation: []
      },
      loading = false,
      isNullishItem = false,
      isFirstItem = true,
      createSession = async () => ({ payload: null }),
      updateSession = async () => ({ payload: null }),
      toggleSession = () => {},
      deleteSession = () => {}
    }: TCostSessionItemProps, ref
  ) => {
    const [fullFreeServiceEnabled, setFullFreeServiceEnabled] = useState<boolean>(false);
    const [freeMinutesEnabled, setFreeMinutesEnabled] = useState<boolean>(false);

    const [form] = Form.useForm<TFormValue>();
    const rate = Form.useWatch('rate', { form, preserve: true });
    const call_duration = Form.useWatch('call_duration', { form, preserve: true });

    const getValueByProfit = (profitValue: number): number => {
      return numToFixed(((profitValue) / (1 - (percentFee / 100))) + transactionFee);
    }

    const getValueByRate = (currValue: number): number => {
      return numToFixed(currValue - transactionFee - getServiceFeeValue(currValue));
    }

    const getProfitInitialValue = (currValue: number): string => {
      const fee = percentFee / 100;
      if (currValue * fee <= 0) return `${ currency.symbol }0.2`;
      return numToFixed(getValueByRate(currValue)).toString();
    }

    const getServiceFeeValue = (rateValue: number): number => {
      const fee = percentFee / 100;
      if (rateValue * fee <= 0 || numToFixed(rateValue * fee) <= 0) return 0.01;
      return numToFixed(((rateValue - transactionFee) * fee));
    }

    const parserHandler = (value: string | undefined): number => {
      return Number.parseFloat(replaceCurrencySymbol(value || '', currency.symbol));
    }

    const getNumberField = (value: string | number): number => {
      return Number.parseFloat(replaceCurrencySymbol(`${ value }`, currency.symbol));
    }

    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [sessionType, setSessionType] = useState<TSessionType | '--'>(session?.rate_type ?? '--');

    const timeOptions: {
      value: string,
      label: string,
      disabled?: boolean,
    }[] = sessionType === '--' ? [] : durations[sessionType].map((duration) => ({
      value: `${ duration }`,
      label: `${ duration }`,
      disabled: existDurations[sessionType].some(exDuration => exDuration === duration)
    })).sort((a, b) => (+a?.value - +b?.value));

    const typeOptions: {
      value: string,
      label: string
    }[] = [
      ...(!!durations.audio_call.length ? [{
        value: 'audio_call',
        label: <CustomIcon name={ EIconName.MicOn }/> as any
      }] : []),
      ...(!!durations.video_call.length ? [{
        value: 'video_call',
        label: <CustomIcon name={ EIconName.CameraOn }/> as any
      }] : []),
      ...(!!durations.conversation.length ? [{
        value: 'conversation',
        label: <CustomIcon name={ EIconName.ChatIcon }/> as any
      }] : [])
    ];

    const serviceFee = useMemo(() => numToFixed(getServiceFeeValue(getNumberField(rate)) + transactionFee), [rate]);
    const profitInitialValue = getProfitInitialValue(session?.rates.value ?? 0);

    const toggleAvailableState = (checked: boolean, event: any): void => {
      if (!session || sessionType === '--') return;
      toggleSession({
        worker_id,
        is_active: !session.is_active,
        rate: numToFixed(session.rates.value - serviceFee),
        rate_type: sessionType,
        call_duration: session.call_duration,
        rate_id: session.id,
        prepaid_enabled: session.prepaid_enabled,
        free_service_minutes: session.free_service_minutes,
        free_minutes_enabled: session.free_minutes_enabled,
        full_free_service_enabled: session.full_free_service_enabled,
      }, checked, event);
    }

    const deleteItemHandler = (): void => {
      if (isNullishItem) {
        form.setFieldsValue({
          call_duration: '--',
          rate_type: '--',
          profit: replaceCurrencySymbol(profitInitialValue, currency.symbol),
          rate: numToFixed(session?.rates?.value || (0.2 + transactionFee + 0.01))
        });
        setSessionType('--');
        setHasChanges(false);
        return;
      }
      deleteSession();
    }

    const handleRateChange = (
      currentValue: string | number,
      profitField: keyof TFormValue,
      currentField: keyof TFormValue
    ): void => {
      const profitValue = currentField.includes('profit') ? getValueByProfit(+currentValue) : getValueByRate(+currentValue);
      const savedValue = getNumberField(session?.rates.value ?? 0);
      const hasChanges =
        currentField.includes('profit') ? savedValue !== numToFixed(profitValue) :
          savedValue !== numToFixed(profitValue) + getServiceFeeValue(savedValue);

      form.setFieldValue(profitField, (!profitValue || profitValue < 0) ? 0 : numToFixed(profitValue));
      setHasChanges(hasChanges);
    };

    const onValuesChange = (
      currentValue: { [key in keyof TFormValue]: string | number },
      { rate_type, call_duration, free_service_minutes, prepaid_enabled, free_minutes_enabled, full_free_service_enabled }: TFormValue
    ): void => {
      const isChangeRateType = rate_type !== '--' && rate_type !== session?.rate_type;
      const isChangeDuration = call_duration !== '--' && call_duration !== session?.call_duration;

      const isChangeFreeMinutes = free_service_minutes !== session?.free_service_minutes;
      const isChangePrepaidEnabled = prepaid_enabled !== session?.prepaid_enabled;
      const isChangeFreeMinutesEnabled = free_minutes_enabled !== session?.free_minutes_enabled;
      const isChangeFullFreeServiceEnabled = full_free_service_enabled !== session?.full_free_service_enabled;

      if (
        currentValue?.hasOwnProperty('free_service_minutes') ||
        currentValue?.hasOwnProperty('prepaid_enabled') ||
        currentValue?.hasOwnProperty('free_minutes_enabled') ||
        currentValue?.hasOwnProperty('full_free_service_enabled')
      ) {
        setHasChanges(isChangeFreeMinutes || isChangeFreeMinutesEnabled || isChangeFullFreeServiceEnabled || isChangePrepaidEnabled);
      }

      if (currentValue?.hasOwnProperty('free_minutes_enabled')) {
        setFreeMinutesEnabled(free_minutes_enabled);
      }

      if (currentValue?.hasOwnProperty('full_free_service_enabled')) {
        setFullFreeServiceEnabled(full_free_service_enabled);
      }

      if (currentValue?.hasOwnProperty('rate')) {
        handleRateChange(currentValue?.rate, 'profit', 'rate');
      }

      if (currentValue?.hasOwnProperty('profit')) {
        handleRateChange(currentValue?.profit, 'rate', 'profit');
      }

      if (currentValue?.hasOwnProperty('rate_type')) {
        setSessionType(rate_type);
        form.setFieldValue('call_duration', '--');
        setHasChanges(isChangeRateType);
      }

      if (currentValue?.hasOwnProperty('call_duration')) {
        setHasChanges(isChangeDuration);
      }
    };

    const saveChanges = async (): Promise<void> => {
      const {
        rate_type,
        call_duration,
        profit,
        free_service_minutes,
        free_minutes_enabled,
        prepaid_enabled,
        full_free_service_enabled
      } = form.getFieldsValue();

      if (rate_type === '--') return;

      let response;
      if (session) {
        response = await updateSession({
          worker_id,
          is_active: session.is_active,
          rate: profit,
          rate_type,
          call_duration,
          rate_id: session.id,
          free_service_minutes,
          prepaid_enabled,
          free_minutes_enabled,
          full_free_service_enabled
        });
      } else {
        response = await createSession({
          worker_id,
          rate_type,
          rate: profit,
          call_duration,
          free_service_minutes,
          prepaid_enabled,
          free_minutes_enabled,
          full_free_service_enabled
        });
      }
      if (!response?.payload) return;
      setHasChanges(false);
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      sessionIsNullish(): boolean {
        return isNullishItem || !session;
      },
      resetState(): void {
        form.setFieldsValue({
          call_duration: `${ session?.call_duration ?? '--' }`,
          rate_type: session ? sessionType : undefined,
          profit: replaceCurrencySymbol(profitInitialValue, currency.symbol),
          rate: numToFixed(session?.rates?.value ?? 0.01),
          free_service_minutes: session?.free_service_minutes || 3,
          free_minutes_enabled: session?.free_minutes_enabled || false,
          prepaid_enabled: session?.prepaid_enabled || false,
          full_free_service_enabled: session?.full_free_service_enabled || false
        });
        setHasChanges(false);
        setFullFreeServiceEnabled(!!session?.full_free_service_enabled);
        setFreeMinutesEnabled(!!session?.free_minutes_enabled);
      }
    }));

    useEffect(() => {
      form.setFieldsValue({
        call_duration: `${ session?.call_duration ?? '--' }`,
        rate_type: session?.rate_type,
        profit: replaceCurrencySymbol(profitInitialValue, currency.symbol),
        rate: numToFixed(session?.rates?.value || (0.2 + transactionFee + 0.01)),
        free_service_minutes: session?.free_service_minutes || 3,
        free_minutes_enabled: session?.free_minutes_enabled || false,
        prepaid_enabled: session?.prepaid_enabled || false,
        full_free_service_enabled: session?.full_free_service_enabled || false
      });
      setFullFreeServiceEnabled(!!session?.full_free_service_enabled);
      setFreeMinutesEnabled(!!session?.free_minutes_enabled);
    }, [session]);

    return (
      <Form className="cost-session-item" form={ form }
            onValuesChange={ onValuesChange }>
        <div className="cost-session-item--item">
          <div className="edit-price-col edit-price-col--type">
            <div className="col-header">
              Type
            </div>
            <div className="col-body">
              <Form.Item name={ 'rate_type' } initialValue={ sessionType }>
                <DropdownElement options={ typeOptions }
                                 withoutAll={ true }
                                 showSearch={ false }
                                 placeholder={ 'Type' }/>
              </Form.Item>
            </div>
          </div>

          <div className="edit-price-col edit-price-col--duration">
            <div className="col-header">
              Duration, min
            </div>
            <div className="col-body">
              <Form.Item name={ 'call_duration' }>
                <DropdownElement options={ timeOptions }
                                 disabled={ (!session?.rates.value) && ((session && !session?.is_active) || !hasChanges || loading) }
                                 withoutAll={ true }
                                 showSearch={ false }
                                 placeholder={ 'Duration' }/>
              </Form.Item>
            </div>
          </div>

          <div className="edit-price-col edit-price-col--cost">
            <div className="col-header">
              Payable Amount for Clients per sess <CustomTooltip
              title={ 'The amount covers both the Service Cost and the Credit Card Processing Fee, with no hidden or extra charges' }><CustomIcon
              name={ EIconName.Info }
              size={ 'size-md' }/></CustomTooltip>
            </div>
            <div className="col-body">
              <Form.Item name={ 'rate' }>
                <NumberInput
                  disabled={ (!session?.rates.value) && ((session && !session?.is_active) || !hasChanges || loading) || fullFreeServiceEnabled }
                  min={ transactionFee + 0.21 }
                  max={ 10000 }
                  placeholder={ 'Rate' }
                  precision={ 2 }
                  formatter={ (value) => `${ currency.symbol }${ value }` }
                  parser={ parserHandler }
                  controls={ false }/>
              </Form.Item>
              /ses
            </div>
          </div>

          <div className="edit-price-col">
            <div className="col-header"/>
            <div className="col-body col-body--equals">
              =
            </div>
          </div>

          <div className="edit-price-col">
            <div className="col-header">
              Credit Card Processing Fees <CustomTooltip
              title={ 'This includes the charges levied by the Payment System for processing credit card transactions. ' +
                `These fees encompass standard processing costs as well as a fixed ${ currency.symbol }${ transactionFee } ${ currency.name } transaction fee.` }><CustomIcon
              name={ EIconName.Info }
              size={ 'size-md' }/></CustomTooltip>
            </div>
            <div className="col-body">
              { currency.symbol }{ serviceFee } /ses
            </div>
          </div>

          <div className="edit-price-col">
            <div className="col-header"/>
            <div className="col-body col-body--plus">
              +
            </div>
          </div>

          <div className="edit-price-col edit-price-col--profit">
            <div className="col-header">
              Service Cost <CustomTooltip
              title={ 'The amount to be credited into your bank account for provided service to your Clients' }><CustomIcon
              name={ EIconName.Info }
              size={ 'size-md' }/></CustomTooltip>
            </div>
            <div className="col-body">
              <Form.Item name={ 'profit' }>
                <NumberInput
                  disabled={ (!session?.rates.value) && ((session && !session?.is_active) || !hasChanges || loading) || fullFreeServiceEnabled }
                  placeholder={ 'Profit' }
                  min={ 0.2 }
                  max={ 10000 }
                  precision={ 2 }
                  formatter={ (value) => `${ currency.symbol }${ value }` }
                  parser={ parserHandler }
                  controls={ false }/>
              </Form.Item>
              /ses
            </div>
          </div>

          <div className="edit-price-col edit-price-col--action">
            <div className="col-header"/>
            <div className="col-body">
              { (hasChanges && call_duration !== '--') && <>
                <CustomButton disabled={ loading }
                              loading={ loading }
                              size={ 'small' }
                              text={ 'Save' }
                              onClick={ saveChanges }/>
                { !session && <CustomIcon name={ EIconName.Close } onClick={ deleteItemHandler }/> }
              </>
              }
              { !hasChanges && <>
                <SwitchElement disabled={ !session } checked={ !!session?.is_active }
                               onChange={ (checked, event) => toggleAvailableState(checked, event) }/>
                <CustomIcon className={ ((isFirstItem && !session)) ? 'disabled-icon' : '' } name={ EIconName.Close }
                            onClick={ deleteSession }/>
              </> }
            </div>
          </div>
        </div>

        <div className="cost-session-item--item second-row">
          <div className="edit-price-col">
            <div className="col-header">
              Free minutes, min <CustomTooltip
              title={ 'Free minutes' }><CustomIcon
              name={ EIconName.Info }
              size={ 'size-md' }/></CustomTooltip>
            </div>
            <div className="col-body">
              <Form.Item name={ 'free_service_minutes' } initialValue={ session?.free_service_minutes || 3 }>
                <NumberInput
                  disabled={ (!session?.rates.value) && ((session && !session?.is_active) || !hasChanges || loading) || fullFreeServiceEnabled }
                  placeholder={ 'Free minutes' }
                  min={ 3 }
                  max={ 999 }
                  parser={ (displayValue) => displayValue ? Math.round(+displayValue) : 0 }
                  controls={ false }/>
              </Form.Item>
              /min
            </div>
          </div>
          <div className="edit-price-col edit-price-col--action">
            <div className="col-header">
              Enable free minutes
            </div>
            <div className="col-body">
              <Form.Item name={ 'free_minutes_enabled' } initialValue={ !!session?.is_active } valuePropName="checked">
                <SwitchElement
                  disabled={ (!session?.rates.value) && ((session && !session?.is_active) || !hasChanges || loading) || fullFreeServiceEnabled }/>
              </Form.Item>
            </div>
          </div>
          <div className="edit-price-col edit-price-col--action">
            <div className="col-header">
              Prepay <CustomTooltip
              title={ 'Payment System will pre-authorize funds before the start of the call' }><CustomIcon
              name={ EIconName.Info }
              size={ 'size-md' }/></CustomTooltip>
            </div>
            <div className="col-body">
              <Form.Item name={ 'prepaid_enabled' } initialValue={ !!session?.is_active } valuePropName="checked">
                <SwitchElement
                  disabled={ (!session?.rates.value) && ((session && !session?.is_active) || !hasChanges || loading) || fullFreeServiceEnabled || !freeMinutesEnabled }/>
              </Form.Item>
            </div>
          </div>
          <div className="edit-price-col edit-price-col--action">
            <div className="col-header">
              Full free session
            </div>
            <div className="col-body">
              <Form.Item name={ 'full_free_service_enabled' } initialValue={ !!session?.is_active }
                         valuePropName="checked">
                <SwitchElement
                  disabled={ (!session?.rates.value) && ((session && !session?.is_active) || !hasChanges || loading) }/>
              </Form.Item>
            </div>
          </div>
        </div>
      </Form>
    );
  }
)
